import { getSession, setActiveEmployee, clearActiveEmployee } from '../session.js';
import { showToast } from './Toast.js';
import type { Employee, EmployeeRole, Transaction } from '../../core/app-store.js';

type NavigateFn = (route: string, params?: Record<string, string>) => void;

// Simple SHA-256 for browser
async function hashPin(pin: string): Promise<string> {
  const msgUint8 = new TextEncoder().encode(`preuve:${pin}`);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgUint8);
  return Array.from(new Uint8Array(hashBuffer)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function renderEmployeeManagement(container: HTMLElement, navigate: NavigateFn): Promise<void> {
  const session = getSession();
  const businessId = session?.businessId ?? 'preview';
  const industry = session?.industry ?? 'general';
  const isConstruction = industry === 'construction';

  const roleOptions = isConstruction
    ? [
        ['foreman', 'Foreman'],
        ['laborer', 'Laborer'],
        ['operator', 'Equipment Operator'],
        ['carpenter', 'Carpenter'],
        ['electrician', 'Electrician'],
        ['plumber', 'Plumber'],
        ['safety', 'Safety Officer'],
        ['manager', 'Project Manager'],
        ['admin', 'Office / Admin'],
        ['other', 'Other'],
      ]
    : [
        ['server', 'Server'],
        ['bartender', 'Bartender'],
        ['host', 'Host / Hostess'],
        ['kitchen', 'Kitchen Staff'],
        ['cashier', 'Cashier'],
        ['manager', 'Manager'],
        ['other', 'Other'],
      ];

  const complianceBanner = isConstruction
    ? `<div class="card" style="margin-bottom:20px;border-left:4px solid var(--brand-blue-light);padding:12px 16px;display:flex;align-items:center;gap:12px">
        <i class="bi bi-hard-hat" style="font-size:20px;color:var(--brand-blue-light);flex-shrink:0"></i>
        <div>
          <div style="font-size:13px;font-weight:700;color:var(--brand-blue-light)">Washington State Construction — Labor Rules</div>
          <div style="font-size:12px;color:var(--text-muted)">
            WA minimum wage: <strong style="color:var(--text-secondary)">$16.66/hr</strong> (2026). Public works projects require
            <strong style="color:var(--text-secondary)">prevailing wage</strong> set by L&amp;I — rates vary by county and trade.
            Overtime applies after <strong style="color:var(--text-secondary)">40 hours/week</strong> at 1.5×. No tip credit applies in this industry.
          </div>
        </div>
      </div>`
    : `<div class="card" style="margin-bottom:20px;border-left:4px solid var(--brand-gold);padding:12px 16px;display:flex;align-items:center;gap:12px">
        <i class="bi bi-info-circle-fill" style="font-size:20px;color:var(--brand-gold);flex-shrink:0"></i>
        <div>
          <div style="font-size:13px;font-weight:700;color:var(--brand-gold)">Washington State Tip Rules</div>
          <div style="font-size:12px;color:var(--text-muted)">
            WA is a <strong style="color:var(--text-secondary)">no tip credit</strong> state — all employees must be paid at least $16.66/hr regardless of tips.
            Tip pooling is allowed but may <strong style="color:var(--text-secondary)">not include managers</strong> or supervisors with hire/fire authority.
            Service charges (auto-gratuity) are taxable wages if paid to employees.
          </div>
        </div>
      </div>`;

  container.innerHTML = `
    <div class="page-header">
      <div style="display:flex;justify-content:space-between;align-items:flex-start">
        <div>
          <h1 class="page-title"><i class="bi bi-person-badge"></i> Employee Management</h1>
          <p class="page-subtitle">${isConstruction ? 'Manage crew accounts, PINs, and wage compliance.' : 'Manage staff accounts, PINs, and tip tracking.'}</p>
        </div>
        <div style="display:flex;gap:10px">
          <button class="btn btn-ghost" id="emp-login-btn">
            <i class="bi bi-key"></i> Employee Login
          </button>
          <button class="btn btn-gold" id="add-emp-btn">
            <i class="bi bi-person-plus"></i> Add Employee
          </button>
        </div>
      </div>
    </div>

    ${complianceBanner}

    <!-- Add/Edit Employee Modal -->
    <div id="emp-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:1000;align-items:center;justify-content:center">
      <div class="card" style="width:440px">
        <h3 style="font-size:15px;font-weight:700;margin-bottom:16px" id="emp-modal-title">Add Employee</h3>
        <div class="form-row-2">
          <div>
            <label>Full Name *</label>
            <input type="text" id="emp-name" placeholder="${isConstruction ? 'Mike Torres' : 'Jane Smith'}" />
          </div>
          <div>
            <label>Role *</label>
            <select id="emp-role">
              ${roleOptions.map(([v, l]) => `<option value="${v}">${l}</option>`).join('')}
            </select>
          </div>
        </div>
        <div class="form-row-2">
          <div>
            <label>4-Digit PIN *</label>
            <input type="password" id="emp-pin" placeholder="••••" maxlength="4" minlength="4"
              style="letter-spacing:4px;font-size:18px;text-align:center" />
          </div>
          <div>
            <label>Hourly Rate ($)</label>
            <input type="number" id="emp-rate" placeholder="16.66" min="16.66" step="0.01" />
          </div>
        </div>
        <div style="font-size:11px;color:var(--text-muted);margin-bottom:12px">
          <i class="bi bi-shield-lock"></i> PIN is hashed and never stored in plain text.
          WA minimum wage: $16.66/hr.
        </div>
        <div style="display:flex;gap:10px;justify-content:flex-end">
          <button class="btn btn-ghost" id="close-emp-modal">Cancel</button>
          <button class="btn btn-gold" id="save-emp-btn">Save Employee</button>
        </div>
      </div>
    </div>

    <!-- Employee PIN Login Modal -->
    <div id="emp-login-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.8);z-index:1000;align-items:center;justify-content:center">
      <div class="card" style="width:360px;text-align:center">
        <div style="font-size:36px;margin-bottom:8px"><i class="bi bi-person-circle"></i></div>
        <h3 style="font-size:16px;font-weight:700;margin-bottom:4px">Employee Sign In</h3>
        <p style="font-size:12px;color:var(--text-muted);margin-bottom:20px">Enter your 4-digit PIN</p>
        <!-- PIN pad display -->
        <div id="pin-display" style="display:flex;justify-content:center;gap:12px;margin-bottom:20px">
          ${[0,1,2,3].map((i) => `<div class="pin-dot" data-pos="${i}" style="width:16px;height:16px;border-radius:50%;border:2px solid var(--border);transition:background .15s"></div>`).join('')}
        </div>
        <!-- Numpad -->
        <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:16px" id="numpad">
          ${[1,2,3,4,5,6,7,8,9,'',0,'⌫'].map((n) => `
            <button class="btn btn-ghost numpad-key" data-val="${n}" style="font-size:18px;font-weight:600;height:52px;${n==='' ? 'visibility:hidden' : ''}">${n}</button>
          `).join('')}
        </div>
        <div id="emp-login-name" style="font-size:14px;font-weight:700;color:var(--success);margin-bottom:8px;min-height:24px"></div>
        <div style="display:flex;gap:8px;justify-content:center">
          <button class="btn btn-ghost" id="close-emp-login">Cancel</button>
          <button class="btn btn-gold" id="clear-emp-session" style="display:none">
            <i class="bi bi-person-x"></i> Sign Out Employee
          </button>
        </div>
      </div>
    </div>

    ${!isConstruction ? `
    <!-- Tip Pool Modal -->
    <div id="tip-pool-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:1000;align-items:center;justify-content:center">
      <div class="card" style="width:500px;max-height:80vh;overflow-y:auto">
        <h3 style="font-size:15px;font-weight:700;margin-bottom:4px"><i class="bi bi-cash-coin"></i> Tip Pool Distribution</h3>
        <p style="font-size:12px;color:var(--text-muted);margin-bottom:16px">Distribute collected tips among eligible employees (excludes managers per WA law).</p>
        <div class="form-row">
          <label>Total Tips to Distribute ($)</label>
          <input type="number" id="tip-total" min="0" step="0.01" placeholder="0.00" />
        </div>
        <div id="tip-emp-list" style="margin-bottom:16px"></div>
        <div id="tip-preview" style="background:var(--bg-tertiary);border-radius:var(--radius-sm);padding:12px;font-size:12px;margin-bottom:16px"></div>
        <div style="display:flex;gap:10px;justify-content:flex-end">
          <button class="btn btn-ghost" id="close-tip-pool">Cancel</button>
          <button class="btn btn-gold" id="distribute-tips-btn">
            <i class="bi bi-check-circle"></i> Confirm Distribution
          </button>
        </div>
      </div>
    </div>
    ` : ''}

    <div id="emp-list-container">
      <div class="empty-state"><div class="spinner"></div></div>
    </div>
  `;

  let editingEmpId: string | null = null;

  const openEmpModal = (emp?: Employee) => {
    editingEmpId = emp?.id ?? null;
    (document.getElementById('emp-modal-title') as HTMLElement).textContent = emp ? 'Edit Employee' : 'Add Employee';
    (document.getElementById('emp-name') as HTMLInputElement).value = emp?.name ?? '';
    (document.getElementById('emp-role') as HTMLSelectElement).value = emp?.role ?? 'server';
    (document.getElementById('emp-pin') as HTMLInputElement).value = '';
    (document.getElementById('emp-rate') as HTMLInputElement).value = emp?.hourlyRate ? String(emp.hourlyRate) : '';
    document.getElementById('emp-modal')!.style.display = 'flex';
    setTimeout(() => (document.getElementById('emp-name') as HTMLInputElement).focus(), 50);
  };

  document.getElementById('add-emp-btn')!.addEventListener('click', () => openEmpModal());
  document.getElementById('close-emp-modal')!.addEventListener('click', () => {
    document.getElementById('emp-modal')!.style.display = 'none';
  });

  document.getElementById('save-emp-btn')!.addEventListener('click', async () => {
    const name = (document.getElementById('emp-name') as HTMLInputElement).value.trim();
    const role = (document.getElementById('emp-role') as HTMLSelectElement).value as EmployeeRole;
    const pin = (document.getElementById('emp-pin') as HTMLInputElement).value.trim();
    const hourlyRate = parseFloat((document.getElementById('emp-rate') as HTMLInputElement).value) || 16.66;

    if (!name) { showToast('Name required', 'error'); return; }
    if (!editingEmpId && pin.length !== 4) { showToast('PIN must be exactly 4 digits', 'error'); return; }

    try {
      const api = getAPI();
      if (editingEmpId) {
        const updates: Partial<Employee> = { name, role, hourlyRate };
        if (pin.length === 4) updates.pinHash = await hashPin(pin);
        await api.updateEmployee(editingEmpId, updates);
        showToast('Employee updated', 'success');
      } else {
        if (hourlyRate < 16.66) {
          showToast('Warning: WA minimum wage is $16.66/hr', 'info');
        }
        const pinHash = await hashPin(pin);
        await api.createEmployee({ businessId, name, pinHash, role, active: true, hourlyRate });
        showToast('Employee added', 'success');
      }
    } catch {
      showToast('Saved (preview mode)', 'info');
    }
    document.getElementById('emp-modal')!.style.display = 'none';
    editingEmpId = null;
    await loadEmployees(businessId, isConstruction, openEmpModal, navigate);
  });

  // Employee PIN login modal
  let pinBuffer = '';

  const updatePinDisplay = () => {
    document.querySelectorAll('.pin-dot').forEach((dot, i) => {
      (dot as HTMLElement).style.background = i < pinBuffer.length ? 'var(--brand-gold)' : '';
      (dot as HTMLElement).style.borderColor = i < pinBuffer.length ? 'var(--brand-gold)' : 'var(--border)';
    });
  };

  const resetPinLogin = () => {
    pinBuffer = '';
    updatePinDisplay();
    (document.getElementById('emp-login-name') as HTMLElement).textContent = '';
  };

  document.getElementById('emp-login-btn')!.addEventListener('click', () => {
    resetPinLogin();
    const cur = session?.employeeName;
    if (cur) {
      (document.getElementById('emp-login-name') as HTMLElement).textContent = `Signed in as: ${cur}`;
      (document.getElementById('clear-emp-session') as HTMLElement).style.display = '';
    } else {
      (document.getElementById('clear-emp-session') as HTMLElement).style.display = 'none';
    }
    document.getElementById('emp-login-modal')!.style.display = 'flex';
  });

  document.getElementById('close-emp-login')!.addEventListener('click', () => {
    document.getElementById('emp-login-modal')!.style.display = 'none';
  });

  document.getElementById('clear-emp-session')!.addEventListener('click', () => {
    clearActiveEmployee();
    showToast('Employee signed out', 'info');
    document.getElementById('emp-login-modal')!.style.display = 'none';
  });

  document.getElementById('numpad')!.querySelectorAll('.numpad-key').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const val = btn.getAttribute('data-val')!;
      if (val === '⌫') {
        pinBuffer = pinBuffer.slice(0, -1);
        updatePinDisplay();
        return;
      }
      if (val === '' || pinBuffer.length >= 4) return;
      pinBuffer += val;
      updatePinDisplay();

      if (pinBuffer.length === 4) {
        const pinHash = await hashPin(pinBuffer);
        try {
          const emp = await getAPI().findEmployeeByPin(businessId, pinHash) as Employee | null;
          if (emp) {
            setActiveEmployee(emp.id, emp.name, emp.role);
            (document.getElementById('emp-login-name') as HTMLElement).textContent = `✓ Welcome, ${emp.name}!`;
            (document.getElementById('emp-login-name') as HTMLElement).style.color = 'var(--success)';
            (document.getElementById('clear-emp-session') as HTMLElement).style.display = '';
            showToast(`Signed in as ${emp.name}`, 'success');
            setTimeout(() => {
              document.getElementById('emp-login-modal')!.style.display = 'none';
            }, 1200);
          } else {
            (document.getElementById('emp-login-name') as HTMLElement).textContent = 'Incorrect PIN';
            (document.getElementById('emp-login-name') as HTMLElement).style.color = 'var(--danger)';
            setTimeout(resetPinLogin, 1000);
          }
        } catch {
          // Preview mode — accept any 4-digit PIN
          (document.getElementById('emp-login-name') as HTMLElement).textContent = '✓ Signed in (preview)';
          setTimeout(() => { document.getElementById('emp-login-modal')!.style.display = 'none'; }, 1200);
        }
      }
    });
  });

  if (!isConstruction) {
    document.getElementById('close-tip-pool')!.addEventListener('click', () => {
      document.getElementById('tip-pool-modal')!.style.display = 'none';
    });
  }

  await loadEmployees(businessId, isConstruction, openEmpModal, navigate);
}

async function loadEmployees(
  businessId: string,
  isConstruction: boolean,
  openEmpModal: (emp?: Employee) => void,
  navigate: NavigateFn
): Promise<void> {
  let employees: Employee[] = [];
  let transactions: Transaction[] = [];
  try {
    [employees, transactions] = await Promise.all([
      getAPI().getEmployees(businessId) as Promise<Employee[]>,
      getAPI().getTransactions(businessId) as Promise<Transaction[]>,
    ]);
  } catch {
    employees = getSampleEmployees(businessId, isConstruction);
    transactions = isConstruction ? [] : getSampleTipTransactions(businessId);
  }

  // Build tip totals per employee from transactions
  const tipsByEmployee = new Map<string, { today: number; week: number; allTime: number }>();
  if (!isConstruction) {
    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);
    const weekAgo = new Date(now); weekAgo.setDate(now.getDate() - 7);
    transactions.forEach((t: Transaction) => {
      if (!t.serverId || !(t.tip ?? 0)) return;
      const entry = tipsByEmployee.get(t.serverId) ?? { today: 0, week: 0, allTime: 0 };
      const tipAmt = t.tip ?? 0;
      entry.allTime += tipAmt;
      if (t.createdAt?.slice(0, 10) === todayStr) entry.today += tipAmt;
      if (t.createdAt >= weekAgo.toISOString()) entry.week += tipAmt;
      tipsByEmployee.set(t.serverId, entry);
    });
  }

  const container = document.getElementById('emp-list-container')!;

  if (employees.length === 0) {
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon"><i class="bi bi-people" style="font-size:48px"></i></div>
        <div class="empty-state-title">No Employees Yet</div>
        <div class="empty-state-desc">${isConstruction ? 'Add crew members so they can clock in with their PIN.' : 'Add employees so they can log in with their PIN and track tips.'}</div>
      </div>
    `;
    return;
  }

  const eligible = employees.filter((e) => e.role !== 'manager' && e.active);
  const roleColor: Record<string, string> = {
    server: '#3b82f6', bartender: '#8b5cf6', host: '#06b6d4',
    kitchen: '#f59e0b', cashier: '#10b981', manager: '#ef4444', other: '#64748b',
    foreman: '#f59e0b', laborer: '#3b82f6', operator: '#8b5cf6',
    carpenter: '#06b6d4', electrician: '#f59e0b', plumber: '#3b82f6',
    safety: '#10b981', admin: '#64748b',
  };

  // Tips summary panel (restaurant only)
  const tipSummaryHtml = !isConstruction ? (() => {
    const tippers = employees.filter((e) => tipsByEmployee.has(e.id));
    if (!tippers.length) return '';
    const totalToday = [...tipsByEmployee.values()].reduce((s, v) => s + v.today, 0);
    const totalWeek = [...tipsByEmployee.values()].reduce((s, v) => s + v.week, 0);
    return `
      <div class="card" style="margin-bottom:16px;border-left:3px solid var(--brand-gold)">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
          <div style="font-size:13px;font-weight:700;color:var(--brand-gold)"><i class="bi bi-cash-coin"></i> Tips Earned — Individual Breakdown</div>
          <div style="font-size:12px;color:var(--text-muted)">Today: <strong style="color:var(--text-primary)">$${totalToday.toFixed(2)}</strong> &nbsp;·&nbsp; This Week: <strong style="color:var(--text-primary)">$${totalWeek.toFixed(2)}</strong></div>
        </div>
        <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:10px">
          ${employees.filter((e) => e.active && e.role !== 'kitchen').map((emp) => {
            const tips = tipsByEmployee.get(emp.id) ?? { today: 0, week: 0, allTime: 0 };
            return `
              <div style="background:var(--bg-tertiary);border-radius:8px;padding:12px">
                <div style="font-weight:600;font-size:13px;margin-bottom:6px">${escHtml(emp.name)}</div>
                <div style="font-size:11px;color:var(--text-muted);text-transform:capitalize;margin-bottom:8px">${emp.role}</div>
                <div style="display:grid;grid-template-columns:1fr 1fr;gap:4px;font-size:12px">
                  <span style="color:var(--text-muted)">Today</span>
                  <span style="text-align:right;font-weight:600;color:${tips.today > 0 ? 'var(--success)' : 'var(--text-muted)'}">$${tips.today.toFixed(2)}</span>
                  <span style="color:var(--text-muted)">This Week</span>
                  <span style="text-align:right;font-weight:600;color:var(--brand-gold)">$${tips.week.toFixed(2)}</span>
                  <span style="color:var(--text-muted)">All Time</span>
                  <span style="text-align:right;font-weight:600">$${tips.allTime.toFixed(2)}</span>
                </div>
              </div>
            `;
          }).join('')}
        </div>
      </div>
    `;
  })() : '';

  container.innerHTML = `
    ${tipSummaryHtml}
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
      <div style="font-size:13px;color:var(--text-muted)">${employees.length} employee${employees.length !== 1 ? 's' : ''}${!isConstruction ? ` · ${eligible.length} tip-pool eligible` : ''}</div>
      ${!isConstruction ? `<button class="btn btn-ghost btn-sm" id="tip-pool-btn"><i class="bi bi-cash-coin"></i> Distribute Tips</button>` : ''}
    </div>
    <div class="card" style="padding:0;overflow:hidden">
      <table style="width:100%;border-collapse:collapse">
        <thead>
          <tr style="background:var(--bg-tertiary);font-size:11px;text-transform:uppercase;letter-spacing:.5px;color:var(--text-muted)">
            <th style="padding:10px 16px;text-align:left">Employee</th>
            <th style="padding:10px 16px;text-align:left">Role</th>
            <th style="padding:10px 16px;text-align:right">Hourly Rate</th>
            ${!isConstruction ? `
            <th style="padding:10px 16px;text-align:right">Tips Today</th>
            <th style="padding:10px 16px;text-align:right">Tips (7d)</th>
            <th style="padding:10px 16px;text-align:center">Tip Eligible</th>
            ` : ''}
            <th style="padding:10px 16px;text-align:center">Status</th>
            <th style="padding:10px 8px"></th>
          </tr>
        </thead>
        <tbody>
          ${employees.map((emp) => {
            const tips = tipsByEmployee.get(emp.id) ?? { today: 0, week: 0, allTime: 0 };
            return `
            <tr style="border-top:1px solid var(--border);font-size:13px">
              <td style="padding:12px 16px">
                <div style="font-weight:600">${escHtml(emp.name)}</div>
                <div style="font-size:10px;color:var(--text-muted)">Since ${emp.createdAt?.slice(0,10) ?? '—'}</div>
              </td>
              <td style="padding:12px 16px">
                <span style="background:${roleColor[emp.role] ?? '#64748b'}22;color:${roleColor[emp.role] ?? '#64748b'};padding:3px 8px;border-radius:12px;font-size:11px;font-weight:600;text-transform:capitalize">${emp.role}</span>
              </td>
              <td style="padding:12px 16px;text-align:right;font-weight:600">
                $${(emp.hourlyRate ?? 16.66).toFixed(2)}/hr
                ${(emp.hourlyRate ?? 16.66) < 16.66 ? `<div style="font-size:10px;color:var(--danger)"><i class="bi bi-exclamation-triangle"></i> Below WA min wage</div>` : ''}
              </td>
              ${!isConstruction ? `
              <td style="padding:12px 16px;text-align:right;font-weight:600;color:${tips.today > 0 ? 'var(--success)' : 'var(--text-muted)'}">
                $${tips.today.toFixed(2)}
              </td>
              <td style="padding:12px 16px;text-align:right;font-weight:600;color:var(--brand-gold)">
                $${tips.week.toFixed(2)}
              </td>
              <td style="padding:12px 16px;text-align:center">
                ${emp.role === 'manager'
                  ? `<span style="color:var(--danger);font-size:11px"><i class="bi bi-x-circle"></i> Not eligible</span>`
                  : `<span style="color:var(--success);font-size:11px"><i class="bi bi-check-circle"></i> Eligible</span>`}
              </td>
              ` : ''}
              <td style="padding:12px 16px;text-align:center">
                <span style="color:${emp.active ? 'var(--success)' : 'var(--text-muted)'};font-size:11px">
                  <i class="bi bi-circle-fill" style="font-size:8px"></i> ${emp.active ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td style="padding:12px 8px;white-space:nowrap">
                <button class="btn btn-ghost btn-sm" data-action="edit" data-id="${emp.id}">Edit</button>
                <button class="btn btn-ghost btn-sm" data-action="toggle" data-id="${emp.id}" style="margin-left:4px">${emp.active ? 'Deactivate' : 'Activate'}</button>
              </td>
            </tr>
          `;}).join('')}
        </tbody>
      </table>
    </div>
  `;

  if (!isConstruction) {
    document.getElementById('tip-pool-btn')!.addEventListener('click', () => openTipPool(employees));
  }

  container.querySelectorAll('[data-action]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const action = btn.getAttribute('data-action')!;
      const id = btn.getAttribute('data-id')!;
      if (action === 'edit') {
        const emp = employees.find((e) => e.id === id);
        if (emp) openEmpModal(emp);
      } else if (action === 'toggle') {
        const emp = employees.find((e) => e.id === id);
        if (emp) {
          try { await getAPI().updateEmployee(id, { active: !emp.active }); } catch { /* preview */ }
          await loadEmployees(businessId, isConstruction, openEmpModal, navigate);
        }
      }
    });
  });
}

function openTipPool(employees: Employee[]): void {
  const eligible = employees.filter((e) => e.role !== 'manager' && e.active);
  const modal = document.getElementById('tip-pool-modal')!;
  const listEl = document.getElementById('tip-emp-list')!;

  listEl.innerHTML = eligible.map((emp) => `
    <div style="display:flex;align-items:center;gap:8px;padding:8px 0;border-bottom:1px solid var(--border)">
      <input type="checkbox" id="tip-emp-${emp.id}" checked class="tip-emp-check" data-id="${emp.id}" />
      <label for="tip-emp-${emp.id}" style="display:flex;align-items:center;gap:6px;flex:1;cursor:pointer">
        <span style="font-weight:600;font-size:13px">${escHtml(emp.name)}</span>
        <span style="font-size:11px;color:var(--text-muted);text-transform:capitalize">${emp.role}</span>
      </label>
      <input type="number" id="tip-pts-${emp.id}" value="1" min="0.1" step="0.5" style="width:60px;text-align:center;font-size:12px" title="Points weight" />
      <span style="font-size:10px;color:var(--text-muted)">pts</span>
    </div>
  `).join('') || `<div style="color:var(--text-muted);font-size:13px;padding:12px 0">No eligible employees found.</div>`;

  const previewEl = document.getElementById('tip-preview')!;
  const update = () => {
    const total = parseFloat((document.getElementById('tip-total') as HTMLInputElement).value) || 0;
    const checked = eligible.filter((e) => (document.getElementById(`tip-emp-${e.id}`) as HTMLInputElement)?.checked);
    const totalPts = checked.reduce((s, e) => s + (parseFloat((document.getElementById(`tip-pts-${e.id}`) as HTMLInputElement)?.value) || 1), 0);
    if (!total || !totalPts || !checked.length) { previewEl.textContent = 'Enter a tip total to preview distribution.'; return; }
    previewEl.innerHTML = `<strong style="color:var(--brand-gold)">Preview:</strong><br>` +
      checked.map((e) => {
        const pts = parseFloat((document.getElementById(`tip-pts-${e.id}`) as HTMLInputElement)?.value) || 1;
        const share = (total * pts / totalPts).toFixed(2);
        return `<span style="display:flex;justify-content:space-between"><span>${escHtml(e.name)}</span><span style="color:var(--success)">$${share}</span></span>`;
      }).join('');
  };

  document.getElementById('tip-total')!.addEventListener('input', update);
  listEl.querySelectorAll('.tip-emp-check, [id^="tip-pts-"]').forEach((el) => el.addEventListener('change', update));

  document.getElementById('distribute-tips-btn')!.addEventListener('click', () => {
    showToast('Tips distributed and recorded', 'success');
    modal.style.display = 'none';
  });

  modal.style.display = 'flex';
  update();
}

function getSampleEmployees(businessId: string, isConstruction: boolean): Employee[] {
  const now = new Date().toISOString();
  if (isConstruction) {
    return [
      { id: 'e1', businessId, name: 'Mike Torres', pinHash: 'demo', role: 'foreman' as EmployeeRole, active: true, createdAt: now, hourlyRate: 28.00 },
      { id: 'e2', businessId, name: 'James Reyes', pinHash: 'demo', role: 'carpenter' as EmployeeRole, active: true, createdAt: now, hourlyRate: 22.50 },
      { id: 'e3', businessId, name: 'Luis Mendoza', pinHash: 'demo', role: 'laborer' as EmployeeRole, active: true, createdAt: now, hourlyRate: 18.00 },
      { id: 'e4', businessId, name: 'Dan Schmidt', pinHash: 'demo', role: 'electrician' as EmployeeRole, active: true, createdAt: now, hourlyRate: 35.00 },
      { id: 'e5', businessId, name: 'Priya Patel', pinHash: 'demo', role: 'safety' as EmployeeRole, active: false, createdAt: now, hourlyRate: 26.00 },
    ];
  }
  return [
    { id: 'e1', businessId, name: 'Alex Johnson', pinHash: 'demo', role: 'server', active: true, createdAt: now, hourlyRate: 16.66 },
    { id: 'e2', businessId, name: 'Maria Santos', pinHash: 'demo', role: 'bartender', active: true, createdAt: now, hourlyRate: 16.66 },
    { id: 'e3', businessId, name: 'Derek Lee', pinHash: 'demo', role: 'kitchen', active: true, createdAt: now, hourlyRate: 18.00 },
    { id: 'e4', businessId, name: 'Sandra Kim', pinHash: 'demo', role: 'manager', active: true, createdAt: now, hourlyRate: 22.00 },
    { id: 'e5', businessId, name: 'Tom Rivera', pinHash: 'demo', role: 'host', active: false, createdAt: now, hourlyRate: 16.66 },
  ];
}

function getSampleTipTransactions(businessId: string): Transaction[] {
  const now = new Date();
  const t = (hoursAgo: number) => new Date(now.getTime() - hoursAgo * 3600000).toISOString();
  return [
    { id: 'tt1', businessId, amount: 78.50, tip: 15.70, serverId: 'e1', serverName: 'Alex Johnson', paymentMethod: 'card', items: [], createdAt: t(1) },
    { id: 'tt2', businessId, amount: 45.00, tip: 9.00,  serverId: 'e1', serverName: 'Alex Johnson', paymentMethod: 'card', items: [], createdAt: t(3) },
    { id: 'tt3', businessId, amount: 92.00, tip: 20.00, serverId: 'e2', serverName: 'Maria Santos', paymentMethod: 'card', items: [], createdAt: t(2) },
    { id: 'tt4', businessId, amount: 34.00, tip: 7.00,  serverId: 'e2', serverName: 'Maria Santos', paymentMethod: 'cash', items: [], createdAt: t(5) },
    { id: 'tt5', businessId, amount: 120.00,tip: 25.00, serverId: 'e1', serverName: 'Alex Johnson', paymentMethod: 'card', items: [], createdAt: t(30) },
    { id: 'tt6', businessId, amount: 55.00, tip: 11.00, serverId: 'e2', serverName: 'Maria Santos', paymentMethod: 'card', items: [], createdAt: t(48) },
    { id: 'tt7', businessId, amount: 67.25, tip: 13.00, serverId: 'e5', serverName: 'Tom Rivera',   paymentMethod: 'card', items: [], createdAt: t(50) },
    { id: 'tt8', businessId, amount: 41.00, tip: 8.00,  serverId: 'e1', serverName: 'Alex Johnson', paymentMethod: 'card', items: [], createdAt: t(72) },
    { id: 'tt9', businessId, amount: 88.00, tip: 17.60, serverId: 'e2', serverName: 'Maria Santos', paymentMethod: 'card', items: [], createdAt: t(96) },
  ];
}

function getAPI() {
  return (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
}
function escHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
